import type { LucideIcon as LucideIconType } from 'lucide-react';
import type { LayoutBlock } from '@web-studio/shared-schemas';
import { isCtaSocialIconKey } from '../../icons/ctaSocialCatalog';
import { resolveReservationChannelIcon } from '../../icons/ctaLucideMap';
import type { SimpleIcon, SiteSocialNetwork } from '../../icons/socialNetworks';
import { pickBlockField } from '../../lib/localized';
import { pickChrome, type SiteChromeKey } from '../../lib/siteChromeCopy';
import {
  hasContactValue,
  resolveDirectNextChromeKey,
} from './directContactShared';
import { resolveExternalHref, resolveTelHref } from './reservationChannelHref';
import { resolveMessengerCtaSocial } from './resolveMessengerSocial';

export type { SiteChromeKey };
export {
  hasContactValue,
  resolveDirectNextChromeKey,
} from './directContactShared';

export type ReservationCtaVoice = 'third-party' | 'messenger' | 'call';

export interface ReservationChannelAction {
  voice: ReservationCtaVoice;
  href: string;
  label: string;
  labelFieldKey: 'ctaLabel' | 'callCtaLabel';
  /** When set, icon is an EditableContent hit target. */
  iconFieldKey?: 'ctaIcon' | 'callCtaIcon';
  icon: LucideIconType;
  socialIcon?: SiteSocialNetwork | SimpleIcon | null;
  openInNewTab: boolean;
}

/**
 * Build WhatsApp and/or phone CTAs from block content.
 * CTAs appear only when the contact value is actionable (not empty / seed).
 */
export function buildDirectContactActions(
  block: LayoutBlock,
  locale: string,
  _editable: boolean | undefined,
): {
  actions: ReservationChannelAction[];
  nextChromeKey: SiteChromeKey;
  hasMessenger: boolean;
  hasCall: boolean;
} {
  const messengerUrl = pickBlockField(block.content, 'messengerUrl', locale);
  const phoneNumber = pickBlockField(block.content, 'phoneNumber', locale);
  const hasMessenger = hasContactValue(messengerUrl);
  const hasCall = hasContactValue(phoneNumber);

  const actions: ReservationChannelAction[] = [];

  if (hasMessenger) {
    const ctaIcon = pickBlockField(block.content, 'ctaIcon', locale);
    const socialIcon = resolveMessengerCtaSocial(ctaIcon, messengerUrl);
    const lucideKey = isCtaSocialIconKey(ctaIcon) ? undefined : ctaIcon;
    const label =
      pickBlockField(block.content, 'ctaLabel', locale) ||
      pickChrome('reservationCtaMessenger', locale);
    actions.push({
      voice: 'messenger',
      href: resolveExternalHref(messengerUrl),
      label,
      labelFieldKey: 'ctaLabel',
      iconFieldKey: 'ctaIcon',
      icon: resolveReservationChannelIcon('messenger', lucideKey),
      socialIcon,
      openInNewTab: true,
    });
  }

  if (hasCall) {
    const callIcon = pickBlockField(block.content, 'callCtaIcon', locale);
    const label =
      pickBlockField(block.content, 'callCtaLabel', locale) ||
      pickChrome('reservationCtaCall', locale);
    actions.push({
      voice: 'call',
      href: resolveTelHref(phoneNumber),
      label,
      labelFieldKey: 'callCtaLabel',
      iconFieldKey: 'callCtaIcon',
      icon: resolveReservationChannelIcon('call', callIcon || 'Phone'),
      openInNewTab: false,
    });
  }

  return {
    actions,
    nextChromeKey: resolveDirectNextChromeKey(hasMessenger, hasCall),
    hasMessenger,
    hasCall,
  };
}
